import { useEffect, useState } from 'react';
import {
  catchError,
  EMPTY,
  finalize,
  of,
  type Subscription,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import ProblemDetailsDto from '../../../../../dto/error/problem-details-dto';
import { type SiteIdNameDto } from '../../../../../dto/SiteIdNameDto';
import useSecuredApiV2 from '../../../../../hooks/use-secured-api-v2';
import { CHECK_ORDERS_FOR_SITES_ENDPOINT_URL } from '../../../../../utils/endpoints';
import { type LocalDate } from '../../../../../utils/local-date';
import type CustomerOrder from '../../../../../entities/customer-order';
import type Site from '../../../../../entities/site';

interface SitesAlreadyHaveOrdersResult {
  isLoading: boolean;
  // Ids of sites already have orders on the selected Date
  sitesAlreadyHaveOrders: Set<string>;
  error: string;
}

/**
 * @description Given a List of sites, check which of them already have order, other than "excludedCustomerOrder" on  "orderDate"
 * @param orderDate the date on which sites should have an order
 * @param excludedCustomerOrder the order to be excluded from search space
 * @param sites Are the sites we are checking if they have orders other than the excludedCustomerOrder on orderDate
 *
 */
export const useSitesHavingOrdersOnDate = function (
  excludedCustomerOrder: CustomerOrder,
  sites: Site[],
  orderDate?: LocalDate
): SitesAlreadyHaveOrdersResult {
  const DEFAULT_ERROR_MESSAGE =
    'Failed To Get Sites Already Having Orders On ' + orderDate?.toString();

  const [sitesAlreadyHaveOrders, setSitesAlreadyHaveOrders] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const api = useSecuredApiV2();

  const sitesIds = sites?.map((site) => site.id) ?? [];

  /**
   *
   * @param sitesIds The sites we want to check if the have orders on orderDate
   * @param orderDate
   * @returns Verdict about the correctness of data
   */
  const isDataValid = function (
    sitesIds: Array<string | undefined>,
    orderDate: LocalDate | undefined
  ): boolean {
    if (!orderDate || sitesIds.length < 1) {
      return false;
    }
    return true;
  };

  /**
   *  Given a List of sites, check which of them already have order, other than "excludedCustomerOrder" on  "orderDate"
   *
   */
  const sendRequest = function (): Subscription {
    const subscription = api
      .post(
        CHECK_ORDERS_FOR_SITES_ENDPOINT_URL,
        { sites: sitesIds, customerOrderId: excludedCustomerOrder.id },
        { orderDate },
        {}
      )
      .pipe(
        switchMap((response) =>
          response.data ? of(response.data) : throwError(() => new ProblemDetailsDto(response))
        ),
        tap((sites: SiteIdNameDto[]) => {
          const sitesIds = sites.map((site) => site.id.toString());
          setSitesAlreadyHaveOrders(new Set(sitesIds));
        }),
        catchError((error: ProblemDetailsDto) => {
          setError(error.detail ?? DEFAULT_ERROR_MESSAGE);
          return EMPTY;
        }),
        finalize(() => {
          setIsLoading(false);
        })
      )
      .subscribe();
    return subscription;
  };

  useEffect(() => {
    if (isDataValid(sitesIds, orderDate)) {
      const subscription = sendRequest();

      // Cleanup method.
      return () => {
        subscription.unsubscribe();
      };
    }
  }, [excludedCustomerOrder, sites, orderDate]);

  return { isLoading, sitesAlreadyHaveOrders, error };
};
